import { useState, useEffect, useCallback } from 'react'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog'
import {
    Mail,
    Send,
    RefreshCw,
    Copy,
    Clock,
    CheckCircle2,
    XCircle,
    Loader2,
    Link as LinkIcon,
    Plus,
} from 'lucide-react'
import { invitesService, TenantInvite, CreateInvitePayload } from '@/services/invites.service'

export function TenantInvitesPage() {
    const [invites, setInvites] = useState<TenantInvite[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Form state
    const [email, setEmail] = useState('')
    const [planName, setPlanName] = useState('Plano Profissional')
    const [planPrice, setPlanPrice] = useState('299.00')

    const loadInvites = useCallback(async () => {
        try {
            setIsLoading(true)
            const data = await invitesService.listInvites()
            setInvites(data.invites)
        } catch (error) {
            console.error('Erro ao carregar convites:', error)
            toast.error('Erro ao carregar convites')
        } finally {
            setIsLoading(false)
        }
    }, [])

    useEffect(() => {
        loadInvites()
    }, [loadInvites])

    const handleCreateInvite = async () => {
        if (!email || !email.includes('@')) {
            toast.error('Digite um email válido')
            return
        }

        try {
            setIsSubmitting(true)
            const payload: CreateInvitePayload = {
                email,
                plan_details: {
                    plan_name: planName,
                    monthly_price: `R$ ${planPrice}`,
                },
            }

            const result = await invitesService.createInvite(payload)

            toast.success('Convite criado com sucesso!')

            // Copiar link automaticamente
            await navigator.clipboard.writeText(result.link)
            toast.success('Link copiado para a área de transferência!')

            setIsDialogOpen(false)
            setEmail('')
            loadInvites()
        } catch (error: any) {
            console.error('Erro ao criar convite:', error)
            toast.error(error.message || 'Erro ao criar convite')
        } finally {
            setIsSubmitting(false)
        }
    }

    const copyLink = async (token: string) => {
        const link = `${window.location.origin}/register?token=${token}`
        await navigator.clipboard.writeText(link)
        toast.success('Link copiado!')
    }

    const getStatusBadge = (invite: TenantInvite) => {
        if (invite.status === 'accepted') {
            return (
                <Badge className="bg-green-100 text-green-800 gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    Aceito
                </Badge>
            )
        }
        if (!invite.is_valid || invite.status === 'expired') {
            return (
                <Badge className="bg-gray-100 text-gray-600 gap-1">
                    <XCircle className="h-3 w-3" />
                    Expirado
                </Badge>
            )
        }
        return (
            <Badge className="bg-amber-100 text-amber-800 gap-1">
                <Clock className="h-3 w-3" />
                Pendente
            </Badge>
        )
    }

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
    }

    // Stats
    const pendingCount = invites.filter(i => i.status === 'pending' && i.is_valid).length
    const acceptedCount = invites.filter(i => i.status === 'accepted').length
    const expiredCount = invites.filter(i => !i.is_valid || i.status === 'expired').length

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-4xl font-bold tracking-tight text-gray-900">
                        Convites de Cadastro
                    </h1>
                    <p className="text-lg text-muted-foreground">
                        Envie convites para novos clientes se cadastrarem na plataforma
                    </p>
                </div>
                <Button
                    className="flex items-center gap-2"
                    onClick={() => setIsDialogOpen(true)}
                >
                    <Plus className="h-4 w-4" />
                    Novo Convite
                </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-muted-foreground">Pendentes</p>
                                <p className="text-3xl font-bold text-amber-600">{pendingCount}</p>
                            </div>
                            <Clock className="h-8 w-8 text-amber-500" />
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-muted-foreground">Aceitos</p>
                                <p className="text-3xl font-bold text-green-600">{acceptedCount}</p>
                            </div>
                            <CheckCircle2 className="h-8 w-8 text-green-500" />
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-muted-foreground">Expirados</p>
                                <p className="text-3xl font-bold text-gray-600">{expiredCount}</p>
                            </div>
                            <XCircle className="h-8 w-8 text-gray-400" />
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Lista de Convites */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle>Convites Enviados</CardTitle>
                        <Button variant="outline" size="sm" onClick={loadInvites} className="flex items-center gap-2">
                            <RefreshCw className="h-4 w-4" />
                            Atualizar
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                        </div>
                    ) : invites.length === 0 ? (
                        <div className="text-center py-12">
                            <Mail className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                            <p className="text-gray-500">Nenhum convite enviado ainda</p>
                            <Button
                                variant="outline"
                                className="mt-4"
                                onClick={() => setIsDialogOpen(true)}
                            >
                                Enviar Primeiro Convite
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {invites.map((invite) => (
                                <div
                                    key={invite.id}
                                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                                            <Mail className="h-5 w-5 text-sky-600" />
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-900">{invite.email}</p>
                                            <div className="flex items-center gap-3 text-sm text-gray-500">
                                                <span>Criado: {formatDate(invite.created_at)}</span>
                                                <span>Expira: {formatDate(invite.expires_at)}</span>
                                                {invite.plan_details?.plan_name && (
                                                    <span className="text-sky-600">
                                                        {invite.plan_details.plan_name}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        {getStatusBadge(invite)}

                                        {invite.is_valid && invite.status === 'pending' && (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => copyLink(invite.token)}
                                                className="gap-1"
                                            >
                                                <Copy className="h-4 w-4" />
                                                Copiar Link
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Dialog de Novo Convite */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Send className="h-5 w-5 text-sky-600" />
                            Enviar Novo Convite
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Email do Cliente *
                            </label>
                            <Input
                                type="email"
                                placeholder="cliente@exemplo.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                O cliente receberá um link exclusivo para cadastro
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Nome do Plano
                                </label>
                                <Input
                                    placeholder="Plano Profissional"
                                    value={planName}
                                    onChange={(e) => setPlanName(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Valor Mensal (R$)
                                </label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    placeholder="299.00"
                                    value={planPrice}
                                    onChange={(e) => setPlanPrice(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                                <LinkIcon className="h-4 w-4 text-sky-600 mt-0.5" />
                                <div className="text-sm">
                                    <p className="font-medium text-sky-900">Como funciona:</p>
                                    <p className="text-sky-700">
                                        Um link único será gerado e copiado automaticamente. O cliente acessa o link,
                                        preenche os dados e é redirecionado para assinar o contrato.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                            Cancelar
                        </Button>
                        <Button onClick={handleCreateInvite} disabled={isSubmitting} className="gap-2">
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Enviando...
                                </>
                            ) : (
                                <>
                                    <Send className="h-4 w-4" />
                                    Criar Convite
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}

export default TenantInvitesPage
